import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Typography, message, Divider, Select, Modal } from 'antd';
import { UserOutlined, LockOutlined, GoogleOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from "framer-motion";

const { Title } = Typography;


const Login = ({ }) => {
    const [currentFeature, setCurrentFeature] = useState(0);
    const [isSignUpModalVisible, setIsSignUpModalVisible] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();


    const features = [
        {
            id: 1,
            title: t("Easy to Remember Details"),
            description: <><span style={{ color: '#7C3AED', fontWeight: 'bold' }}>{t('Live transcript')}</span> {t('captures every word in real-time')}</>,
            image: "/assets/demo/eli-transcript.png",
        },
        {
            id: 2,
            title: t("Easy to Understand"),
            description: <><span style={{ color: '#7C3AED', fontWeight: 'bold' }}>{t('Real-time translation')}</span> {t('in your preferred language')}</>,
            image: "/assets/demo/eli-translate.png",
        },
        {
            id: 3,
            title: t("Easy to Follow Up"),
            description: <><span style={{ color: '#7C3AED', fontWeight: 'bold' }}>{t('AI-powered live summary')}</span> {t('keeps you focused')}</>,
            image: "/assets/demo/eli-summary.png",
        },
        {
            id: 4,
            title: t("Easy to Highlight"),
            description: <>{t('Mark')} <span style={{ color: '#7C3AED', fontWeight: 'bold' }}>{t('important moments')}</span> {t('with one click')}</>,
            image: "/assets/demo/eli-highlight.png",
        },
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentFeature((prev) => (prev + 1) % features.length);
        }, 10000);
        return () => clearInterval(timer);
    }, [currentFeature]);

    const handleGoogleLogin = async () => {
        const { data, error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: process.env.REACT_APP_REDIRECT_URL
            }
        });

        if (error) {
            message.error(error.message);
        } else {
            message.success(t('Google Login Success!'));
            navigate('/settings');
        }
    };

    const handleEmailLogin = async (values) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email: values.email,
            password: values.password,
        });

        if (error) {
            message.error(error.message);
        } else {
            message.success(t('Login successful!'));
            navigate('/settings');
        }
    };

    const handleSignUp = async (values) => {
        const { data, error } = await supabase.auth.signUp({
            email: values.email,
            password: values.password,
        });

        if (error) {
            message.error(error.message);
        } else {
            message.success(t('Please check your email to confirm your registration!'));
            setIsSignUpModalVisible(false);
        }
    };

    const PrivacyNotice = () => (
        <div style={{
            border: '1px solid #E5E7EB',
            padding: '10px',
            borderRadius: '6px',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        }}>
            <div style={{ color: '#4B5563', fontSize: '20px' }}>🔒</div>
            <Typography.Text type="secondary" style={{
                fontSize: '13px',
                lineHeight: '1.4',
            }}>
                {t('We never use your personal information or meeting data without your explicit permission.')}
            </Typography.Text>
        </div>
    );

    const TermsAndPrivacy = () => (
        <Typography.Text type="secondary" style={{
            fontSize: '13px',
            lineHeight: '1.5',
            display: 'block',
            textAlign: 'center'
        }}>
            {t('By continuing, you agree to our')}{' '}
            <Link to="/terms" style={{
                color: '#7C3AED',
                textDecoration: 'underline',
                fontWeight: 500
            }}>
                {t('Terms of Service')}
            </Link>
            {' '}{t('and')}{' '}
            <Link to="/privacy" style={{
                color: '#7C3AED',
                textDecoration: 'underline',
                fontWeight: 500
            }}>
                {t('Privacy Policy')}
            </Link>
        </Typography.Text>
    );

    const SignUpModal = () => (
        <Modal
            title={t("Sign Up")}
            open={isSignUpModalVisible}
            onCancel={() => setIsSignUpModalVisible(false)}
            footer={null}
        >
            {/* <PrivacyNotice /> */}
            <Form
                form={form}
                onFinish={handleSignUp}
                layout="vertical"
            >
                <Form.Item
                    name="email"
                    rules={[
                        { required: true, message: t('Please input your email!') },
                        { type: 'email', message: t('Please enter a valid email!') }
                    ]}
                >
                    <Input prefix={<UserOutlined />} placeholder={t("Email")} />
                </Form.Item>
                <Form.Item
                    name="password"
                    rules={[
                        { required: true, message: t('Please input your password!') },
                        { min: 6, message: t('Password must be at least 6 characters!') }
                    ]}
                >
                    <Input.Password prefix={<LockOutlined />} placeholder={t("Password")} />
                </Form.Item>
                <Form.Item
                    name="confirmPassword"
                    dependencies={['password']}
                    rules={[
                        { required: true, message: t('Please confirm your password!') },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error(t('The two passwords do not match!')));
                            },
                        }),
                    ]}
                >
                    <Input.Password prefix={<LockOutlined />} placeholder={t("Confirm Password")} />
                </Form.Item>
                <Form.Item>
                    <TermsAndPrivacy />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit" block>
                        {t("Sign Up")}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );

    return (
        <div style={{
            maxWidth: 1000,
            margin: '0 auto',
            marginTop: 40,
            padding: '20px'
        }}>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
                <Title level={2} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontFamily: "'Pretendard', -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif",
                    fontWeight: 700,
                    letterSpacing: '-0.03em'
                }}>
                    {t('Welcome to ELI')} <img src="/assets/icon48.png" alt="ELI" style={{ width: '24px', height: '24px' }} />
                </Title>
                <Typography.Text type="secondary" style={{
                    fontFamily: "'Pretendard', -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif",
                    fontSize: '16px',
                    letterSpacing: '-0.02em'
                }}>
                    {t('Discover how ELI can supercharge your in-meeting performance.')}
                </Typography.Text>
                <Typography.Text style={{
                    display: 'block',
                    marginTop: '8px',
                    color: '#10B981',
                    fontWeight: 500,
                    fontSize: '16px'
                }}>
                    ✨ {t('All these features are completely free')} ✨
                </Typography.Text>
            </div>

            <div style={{
                display: 'flex',
                gap: '60px',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap'
            }}>

                {/* 로그인 섹션 */}
                <div style={{
                    flex: '0 1 300px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: '20px',
                    padding: '30px',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                }}>
                    <Typography.Title level={4} style={{ textAlign: 'center', margin: 0 }}>
                        {t('Get Started')}
                    </Typography.Title>

                    {/* <PrivacyNotice /> */}

                    <Form onFinish={handleEmailLogin}>
                        <Form.Item
                            name="email"
                            rules={[
                                { required: true, message: t('Please input your email!') },
                                { type: 'email', message: t('Please enter a valid email!') }
                            ]}
                        >
                            <Input prefix={<UserOutlined />} placeholder={t("Email")} />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            rules={[{ required: true, message: t('Please input your password!') }]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder={t("Password")} />
                        </Form.Item>
                        <Form.Item>
                            <TermsAndPrivacy />
                        </Form.Item>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" block>
                                {t("Login")}
                            </Button>
                        </Form.Item>
                    </Form>

                    <Divider>{t("OR")}</Divider>

                    <Button
                        icon={<GoogleOutlined />}
                        onClick={handleGoogleLogin}
                        size="large"
                        style={{
                            height: '45px',
                            fontSize: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                    >
                        {t('Continue with Google')}
                    </Button>

                    <Typography.Text style={{ textAlign: 'center', marginTop: '10px' }}>
                        {t("Don't have an account?")} {' '}
                        <Button type="link" onClick={() => setIsSignUpModalVisible(true)}>
                            {t("Sign up")}
                        </Button>
                    </Typography.Text>
                </div>

                {/* New Feature Showcase Section (replacing video) */}
                <div style={{
                    flex: '1 1 560px',
                    maxWidth: 560,
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    padding: '24px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                }}>

                    <AnimatePresence mode="wait">
                        <motion.h4
                            key={currentFeature}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.5 }}
                            style={{
                                fontSize: '24px',
                                fontWeight: 600,
                                color: '#7C3AED',
                                textAlign: 'center',
                                marginBottom: '16px'
                            }}
                        >
                            {features[currentFeature].title}
                        </motion.h4>
                    </AnimatePresence>

                    <AnimatePresence mode="wait">
                        <motion.p
                            key={`desc-${currentFeature}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                            style={{
                                fontSize: '16px',
                                color: '#4B5563',
                                textAlign: 'center',
                                marginBottom: '24px'
                            }}
                        >
                            {features[currentFeature].description}
                        </motion.p>
                    </AnimatePresence>

                    <div style={{ position: 'relative', height: '300px', marginBottom: '24px' }}>
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={`image-${currentFeature}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                                src={features[currentFeature].image}
                                alt="Feature showcase"
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '8px'
                                }}
                            />
                        </AnimatePresence>
                    </div>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '16px'
                    }}>
                        {features.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentFeature(index)}
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    backgroundColor: currentFeature === index ? '#7C3AED' : '#E5E7EB',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.3s'
                                }}
                                aria-label={`Feature ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <SignUpModal />
        </div>
    );
};

export default Login;